import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import {
    fetchFilterOptions,
    fetchBillsData,
    setFromDate,
    setToDate,
    setSelectedBranch,
    resetFilters,
    selectBranches,
    selectSelectedBranch,
    selectFromDate,
    selectToDate,
    selectLoading,
    selectFilterOptionsLoading,
    selectFilterOptionsError
} from '../../store/slices/billReportsSlice';
import './billreports.css';

const ALL_BRANCHES = { branch_id: 'All', branch_name: 'All Branches' };

const BillReportsHeader = () => {
    const dispatch = useDispatch();
    const branchInitialized = useRef(false);

    const role = (localStorage.getItem('role') || '').toUpperCase();
    const isManager = role === 'MANAGER';
    const storedBranchId = localStorage.getItem('branchid') || '';
    const storedBranchName = localStorage.getItem('branch') || storedBranchId;

    // Redux selectors
    const branches = useSelector(selectBranches);
    const selectedBranch = useSelector(selectSelectedBranch);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const loading = useSelector(selectLoading);
    const branchesLoading = useSelector(selectFilterOptionsLoading);
    const branchesError = useSelector(selectFilterOptionsError);

    // Initialize component - fetch the branch list
    useEffect(() => {
        const storedClientid = localStorage.getItem('clientid');
        if (storedClientid) {
            dispatch(fetchFilterOptions({ clientId: storedClientid }));
        }
    }, [dispatch]);

    // MANAGER: branch is hidden and always the manager's own branch.
    // ADMIN/CEO: default to "All Branches" so Apply covers every branch unless narrowed.
    useEffect(() => {
        if (branchInitialized.current || selectedBranch) return;
        if (isManager) {
            if (!storedBranchId) return;
            dispatch(setSelectedBranch({ branch_id: storedBranchId, branch_name: storedBranchName }));
            branchInitialized.current = true;
            return;
        }
        if (!branches.length) return;
        dispatch(setSelectedBranch(branches.find((b) => String(b.branch_id).toLowerCase() === 'all') || ALL_BRANCHES));
        branchInitialized.current = true;
    }, [branches, selectedBranch, isManager, storedBranchId, storedBranchName, dispatch]);

    // Apply filters - fetch bill data
    const handleApplyFilters = () => {
        if (!fromDate || !toDate) {
            return;
        }

        dispatch(
            fetchBillsData({
                fromDate,
                toDate,
                branchId: selectedBranch?.branch_id || 'All',
                status: ''
            })
        );
    };

    // Reset filters
    const handleResetFilters = () => {
        dispatch(resetFilters());
        branchInitialized.current = false;
    };

    return (
        <>
            <div className="mb-3">
                <h2 className="text-3xl font-bold mb-1" style={{ color: 'var(--primary-color)' }}>
                    Bill Reports
                </h2>
                <p className="text-color-secondary mb-0">View and analyze bill reports with advanced filtering options</p>
            </div>

            {/* Filter Section */}
            <Card className="mb-2 bill-reports-filter-card report-filter-uniform">
                <div className="flex flex-wrap align-items-end bill-reports-filter-row">
                    <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                        <label htmlFor="fromDate">From Date <span className="text-red-500">*</span></label>
                        <Calendar id="fromDate" value={fromDate} onChange={(e) => dispatch(setFromDate(e.value))} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                    </div>

                    <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                        <label htmlFor="toDate">To Date <span className="text-red-500">*</span></label>
                        <Calendar id="toDate" value={toDate} onChange={(e) => dispatch(setToDate(e.value))} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                    </div>

                    {!isManager && (
                        <div className="flex flex-column" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                            <label htmlFor="branch">Branch</label>
                            <Dropdown
                                id="branch"
                                value={selectedBranch}
                                options={branches}
                                onChange={(e) => dispatch(setSelectedBranch(e.value || ALL_BRANCHES))}
                                optionLabel="branch_name"
                                dataKey="branch_id"
                                placeholder="All Branches"
                                className="w-full"
                                filter
                                showClear
                                loading={branchesLoading}
                                emptyMessage={branchesError ? 'Could not load branches' : 'No branches found'}
                            />
                            {branchesError && <small className="text-red-500">Branch list failed to load. Reports will cover all branches.</small>}
                        </div>
                    )}

                    <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                        <Button label="Apply" icon="pi pi-check" className="p-button-primary" onClick={handleApplyFilters} disabled={!fromDate || !toDate || loading} loading={loading} />
                        <Button icon="pi pi-refresh" className="p-button-secondary" onClick={handleResetFilters} tooltip="Reset" />
                    </div>
                </div>
            </Card>
        </>
    );
};

export default BillReportsHeader;
